<template>
  <div class="p-4" style="display: flex; gap: 8px; background-color: #e5f0fb">
    <div class="p-4 card" style="max-width: 600px; min-width: 400px" v-if="isAfter2024">
      <div class="d-flex align-items-center" style="gap: 8px">
        <h6 class="m-0">Fluginformationen</h6>
        <b-badge
          style="height: 32px; display: flex; justify-content: center; align-items: center"
          :variant="['RES', 'RES GRP'].includes(einkaufsart) ? 'danger' : ''"
        >
          {{ einkaufsart }}
        </b-badge>
        <b-button class="ml-auto" size="sm" variant="primary" @click="onClickAufpreisRechner">
          Aufpreis-Rechner
        </b-button>
      </div>

      <div class="p-2 mt-3" style="display: flex; gap: 12px">
        <table style="border-radius: 8px">
          <tr v-for="(item, i) in tableDefinition" :key="'tableRow-' + i">
            <td
              v-for="(cell, j) in item"
              :key="'tableRow-' + i + '_tableCell-' + j"
              :style="cell.style"
              :class="cell.class"
            >
              <span v-if="cell.html" v-html="cell.string"></span>
              <span v-else>{{ cell.string }}</span>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <div class="p-4 card" style="min-width: 400px; gap: 24px">
      <div>
        <h6 class="m-0">Verlängerungen</h6>
        <VerlaengerungWidget
          class="p-2"
          :verlaengerungen="verlaengerungen"
          :reiseterminId="reiseterminId"
          v-if="verlaengerungen.length > 0"
        ></VerlaengerungWidget>
        <div v-else class="p-2">Keine Verlängerungen vorhanden!</div>
      </div>
    </div>
    <div class="p-4 card" style="min-width: 400px; gap: 24px">
      <div>
        <h6 class="m-0">Optionals</h6>
        <OptionalsWidget
          v-if="optionals.length > 0"
          class="p-2"
          :optionals="optionals"
          :reiseterminId="reiseterminId"
        ></OptionalsWidget>
        <div v-else-if="qsaetze.length === 0" class="p-2">Keine Optionals vorhanden!</div>

        <div v-if="qsaetze.length > 0" class="mt-3">
          <div class="p-2 d-flex mt-2" style="flex-direction: column; gap: 4px">
            <div v-for="qsatz in qsaetze" :key="qsatz.kuerzel" class="d-flex justify-content-between">
              <div class="mr-2">{{ qsatz.kuerzel }} ({{ qsatz.beschreibung }}):</div>
              <div class="kachel-badge green">J</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import VerlaengerungWidget from '@/components/produkte/reisetermine/verlangerung-widget.vue';
import OptionalsWidget from './optionals-widget.vue';
import { isAfter } from 'date-fns';
import {
  buildFluginformationen,
  buildFluginformationenZeilen,
  formatGruppeneinkaeufeMitFlugnummer,
} from './helpers/fluginformationen';

export default {
  components: { VerlaengerungWidget, OptionalsWidget },
  data() {
    return {
      data: null,
      tableDefinition: null,
    };
  },
  mounted() {
    this.data = this.params.data;

    this.tableDefinition = buildFluginformationenZeilen(this.fluginformationen);
    if (!['RES', 'RES GRP'].includes(this.einkaufsart)) {
      this.tableDefinition.push([
        { string: 'Vorverlängerung', style: '', class: 'row-description' },
        { string: this.fluginformationen.vorverlaengerung, style: '', class: '' },
        { string: '', style: '', class: '' },
      ]);
    }

    if (this.einkaufsart !== 'EP' && this.einkaufsart !== '-') {
      this.tableDefinition.push([
        { string: 'GRP FH (Flugnr.)', style: '', class: 'row-description' },
        { string: this.gruppeFlughafen, style: '', class: '', html: true },
        { string: this.gruppeFlughafenRueck, style: '', class: '', html: true },
      ]);
    }
  },
  computed: {
    isAfter2024() {
      if (this.data) {
        const isAfter2024 = isAfter(new Date(this.data.startdatum), new Date('2024-12-31'));
        return isAfter2024;
      }
      return false;
    },
    reiseterminId() {
      return this.data?.id ?? null;
    },
    verlaengerungen() {
      if (this?.data?.zusatzleistungen) {
        return this.data.zusatzleistungen.filter(
          vl => !vl.isDeleted && vl.typ === 'Verlaengerung' && vl.verfuegbarkeitsStatus !== 'NichtVerfuegbar'
        );
      }
      return [];
    },
    optionals() {
      if (this?.data?.zusatzleistungen) {
        return this.data.zusatzleistungen.filter(
          optional =>
            !optional.isDeleted &&
            optional.typ === 'Optional' &&
            optional.verfuegbarkeitsStatus !== 'NichtVerfuegbar'
        );
      }
      return [];
    },
    qsaetze() {
      if (this?.data?.zusatzleistungen) {
        return this.data.zusatzleistungen.filter(
          item => !item.isDeleted && item.typ === 'Qsatz' && item.verfuegbarkeitsStatus !== 'NichtVerfuegbar'
        );
      }
      return [];
    },
    // Gemeinsame Anzeigewerte der Fluginformationen, geteilt mit dem Aufpreisrechner
    fluginformationen() {
      return buildFluginformationen(this.data);
    },
    einkaufsart() {
      return this.fluginformationen.einkaufsart;
    },
    gruppeFlughafen() {
      return formatGruppeneinkaeufeMitFlugnummer(this.data, 'flugnummerGruppeHin');
    },
    gruppeFlughafenRueck() {
      return formatGruppeneinkaeufeMitFlugnummer(this.data, 'flugnummerGruppeRueck');
    },
  },

  methods: {
    onClickAufpreisRechner(event) {
      event.preventDefault();
      const routeData = this.$router.resolve({ path: '/aufpreis-rechner?reiseterminId=' + this.data.id });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<style scoped>
td {
  padding: 4px 8px;
  width: 150px;
}

tr {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.row-description {
  background-color: var(--border);
}
</style>
